import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import i18n from "@/lib/i18n";
import { queryService } from "@/api/services/query-service";
import { useToast } from "@/features/toast";

export type DictationStatus = "idle" | "recording" | "transcribing";

interface UseDictationOptions {
  // Transkript hazır olduğunda çağrılır. `send` true ise metin kutuya
  // yazılmadan doğrudan gönderilir (şeritteki gönder butonu).
  onTranscribed: (text: string, meta: { send: boolean }) => void;
}

const PREFERRED_MIME_TYPES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4",
];

/** Peak geçmişi — ~6 sn @ ~18 örnek/sn (yavaş, ChatGPT temposu) */
const PEAK_HISTORY_SIZE = 120;
/** Yeni bar ekleme aralığı (ms). Daha yüksek = daha yavaş kayma */
const PEAK_SAMPLE_INTERVAL_MS = 55;

const getSupportedMimeType = (): string | undefined => {
  if (typeof MediaRecorder === "undefined") return undefined;
  return PREFERRED_MIME_TYPES.find((type) =>
    MediaRecorder.isTypeSupported(type)
  );
};

export const useDictation = ({ onTranscribed }: UseDictationOptions) => {
  const { t } = useTranslation();
  const { error: showErrorToast } = useToast();

  const [status, setStatus] = useState<DictationStatus>("idle");
  const [durationMs, setDurationMs] = useState(0);

  // Canvas bu ref'i doğrudan okur — React'i 60fps re-render'a sokmamak için state değil
  const peaksRef = useRef<number[]>([]);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startTimeRef = useRef(0);
  const durationTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const rafRef = useRef<number | null>(null);
  const shouldTranscribeOnStopRef = useRef(false);
  // Bu kaydın transkripti kutuya değil, doğrudan gönderime gidecek.
  const sendAfterTranscribeRef = useRef(false);
  const mimeTypeRef = useRef<string | undefined>(undefined);

  const onTranscribedRef = useRef(onTranscribed);
  onTranscribedRef.current = onTranscribed;

  const stopTimersAndAnalyser = useCallback(() => {
    if (durationTimerRef.current !== null) {
      clearInterval(durationTimerRef.current);
      durationTimerRef.current = null;
    }
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    analyserRef.current = null;
  }, []);

  const releaseStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  }, []);

  const cleanup = useCallback(() => {
    stopTimersAndAnalyser();
    releaseStream();
    mediaRecorderRef.current = null;
    chunksRef.current = [];
    peaksRef.current = [];
    shouldTranscribeOnStopRef.current = false;
    setDurationMs(0);
  }, [releaseStream, stopTimersAndAnalyser]);

  const startAnalyser = useCallback((stream: MediaStream) => {
    try {
      const AudioContextCtor =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext?: typeof AudioContext })
          .webkitAudioContext;
      if (!AudioContextCtor) return;

      const audioContext = new AudioContextCtor();
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 512;
      analyser.smoothingTimeConstant = 0.45;
      source.connect(analyser);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;
      peaksRef.current = [];

      const timeData = new Float32Array(analyser.fftSize);
      let windowMax = 0;
      let lastSampleAt = 0;

      const tick = (now: number) => {
        if (!analyserRef.current) return;
        analyserRef.current.getFloatTimeDomainData(timeData);

        let sumSquares = 0;
        let peak = 0;
        for (let i = 0; i < timeData.length; i++) {
          const v = timeData[i];
          sumSquares += v * v;
          const abs = Math.abs(v);
          if (abs > peak) peak = abs;
        }
        const rms = Math.sqrt(sumSquares / timeData.length);
        // Orta gain: normal konuşma ~0.45–0.65, fısıltı ~0.15, bağırma ~0.9–1.0
        const level = Math.min(1, peak * 0.62 + rms * 1.7);
        if (level > windowMax) windowMax = level;

        // Her frame kaydırma yerine aralıkla örnekle → yavaş, akıcı kayma
        if (now - lastSampleAt >= PEAK_SAMPLE_INTERVAL_MS) {
          const next = peaksRef.current;
          next.push(windowMax);
          if (next.length > PEAK_HISTORY_SIZE) {
            next.splice(0, next.length - PEAK_HISTORY_SIZE);
          }
          windowMax = 0;
          lastSampleAt = now;
        }

        rafRef.current = requestAnimationFrame(tick);
      };

      rafRef.current = requestAnimationFrame(tick);
    } catch (err) {
      console.error("Ses analizi başlatılamadı:", err);
    }
  }, []);

  const start = useCallback(async () => {
    if (status !== "idle") return;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mimeType = getSupportedMimeType();
      mimeTypeRef.current = mimeType;
      const recorder = new MediaRecorder(
        stream,
        mimeType ? { mimeType } : undefined
      );
      chunksRef.current = [];
      shouldTranscribeOnStopRef.current = false;

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      recorder.onstop = async () => {
        const shouldTranscribe = shouldTranscribeOnStopRef.current;
        const blob = new Blob(chunksRef.current, {
          type: mimeTypeRef.current || "audio/webm",
        });

        // Analyser/stream'i kapat ama peak + süre UI'da dursun (spinner altında donuk waveform)
        stopTimersAndAnalyser();
        releaseStream();
        mediaRecorderRef.current = null;
        chunksRef.current = [];

        const send = sendAfterTranscribeRef.current;
        sendAfterTranscribeRef.current = false;

        if (!shouldTranscribe || blob.size === 0) {
          peaksRef.current = [];
          setDurationMs(0);
          setStatus("idle");
          return;
        }

        setStatus("transcribing");
        try {
          const locale = (i18n.language || "en").split("-")[0];
          const supportedLocales = ["en", "tr", "de", "es", "fr", "ar", "it"];
          const language = supportedLocales.includes(locale) ? locale : "en";

          const result = await queryService.transcribeAudio(blob, {
            language,
          });
          onTranscribedRef.current(result.text || "", { send });
        } catch (err) {
          console.error("Dikte transkripsiyon hatası:", err);
          showErrorToast(t("error.dictationFailed"), {
            title: t("error.title"),
          });
        } finally {
          peaksRef.current = [];
          setDurationMs(0);
          setStatus("idle");
        }
      };

      mediaRecorderRef.current = recorder;
      startTimeRef.current = Date.now();
      recorder.start();
      setStatus("recording");
      setDurationMs(0);

      durationTimerRef.current = setInterval(() => {
        setDurationMs(Date.now() - startTimeRef.current);
      }, 100);

      startAnalyser(stream);
    } catch (err) {
      console.error("Mikrofon erişimi hatası:", err);
      cleanup();
      setStatus("idle");
      showErrorToast(t("error.microphoneNotAvailable"), {
        title: t("error.title"),
      });
    }
  }, [
    cleanup,
    releaseStream,
    showErrorToast,
    startAnalyser,
    status,
    stopTimersAndAnalyser,
    t,
  ]);

  const cancel = useCallback(() => {
    shouldTranscribeOnStopRef.current = false;
    sendAfterTranscribeRef.current = false;
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      mediaRecorderRef.current.stop();
    } else {
      cleanup();
      setStatus("idle");
    }
  }, [cleanup]);

  const confirm = useCallback((options?: { send?: boolean }) => {
    shouldTranscribeOnStopRef.current = true;
    sendAfterTranscribeRef.current = options?.send === true;
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      mediaRecorderRef.current.stop();
    }
  }, []);

  useEffect(() => {
    return () => {
      shouldTranscribeOnStopRef.current = false;
      sendAfterTranscribeRef.current = false;
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state !== "inactive"
      ) {
        mediaRecorderRef.current.stop();
      }
      cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    status,
    isRecording: status === "recording",
    isTranscribing: status === "transcribing",
    durationMs,
    peaksRef,
    start,
    cancel,
    confirm,
  };
};
